import { Input } from '@/components/ui/input'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot
} from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'

import { EMAIL_CHANGE_OTP_LENGTH } from './email-change-context'
import { useEmailChange } from './use-email-change'

/** render inside EmailChangeProvider. */
export function ChangeEmailForm() {
  const {
    state,
    dispatch,
    currentEmail,
    codeAddress,
    resendCooldown,
    start,
    confirmCode,
    resend,
    reset
  } = useEmailChange()

  return (
    <div className='flex w-full flex-col gap-4 rounded-2xl border border-border bg-page p-6'>
      <div>
        <h2 className='text-[16px] font-semibold text-ink'>Email</h2>
        <p className='mt-0.5 text-[13px] text-ink-soft'>
          This is the address you sign in with.
        </p>
      </div>

      <div className='flex flex-col gap-1'>
        <span className='text-xs/relaxed font-medium text-ink-soft'>
          Current email
        </span>
        <span className='text-sm text-ink'>{currentEmail}</span>
      </div>

      {state.step === 'done' && (
        <div className='flex flex-col items-start gap-3'>
          <p className='rounded-xl border border-border bg-cream/40 px-4 py-3 text-[13px] text-ink-soft'>
            Your email is now <strong>{currentEmail}</strong>. Use it the next
            time you sign in.
          </p>
          <button
            type='button'
            onClick={reset}
            className='-mx-1 inline-flex min-h-11 cursor-pointer items-center px-1 text-sm font-medium text-accent hover:text-[#15296b] sm:mx-0 sm:min-h-0 sm:px-0 dark:text-accent-mid'
          >
            Change it again
          </button>
        </div>
      )}

      {state.step === 'email' && (
        <form
          onSubmit={event => {
            event.preventDefault()
            void start()
          }}
          className='flex flex-col gap-4'
        >
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='new-email'>New email</Label>
            <Input
              id='new-email'
              type='email'
              placeholder='you@company.com'
              value={state.newEmail}
              onChange={event =>
                dispatch({ type: 'SET_NEW_EMAIL', payload: event.target.value })
              }
            />
          </div>

          {state.error && <p className='text-sm text-red-600'>{state.error}</p>}

          <div className='flex flex-col sm:flex-row sm:justify-end'>
            <button
              type='submit'
              disabled={state.newEmail.trim().length === 0 || state.submitting}
              className='w-full cursor-pointer rounded-xl bg-accent px-6 py-2.5 text-[14px] font-medium text-white transition-all hover:-translate-y-px hover:bg-[#15296b] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:bg-accent sm:w-auto'
            >
              {state.submitting ? 'Sending...' : 'Send code'}
            </button>
          </div>
        </form>
      )}

      {(state.step === 'current-code' || state.step === 'new-code') && (
        <form
          onSubmit={event => {
            event.preventDefault()
            void confirmCode(state.code)
          }}
          className='flex flex-col gap-4'
        >
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='change-code'>Code sent to {codeAddress}</Label>
            <p className='text-[13px] text-ink-soft'>
              {state.step === 'current-code'
                ? 'Confirm you still control your current address.'
                : 'Confirm you can receive mail at the new address.'}
            </p>
            <InputOTP
              id='change-code'
              maxLength={EMAIL_CHANGE_OTP_LENGTH}
              value={state.code}
              onChange={value => dispatch({ type: 'SET_CODE', payload: value })}
              disabled={state.submitting}
              autoFocus
            >
              <InputOTPGroup>
                {Array.from({ length: EMAIL_CHANGE_OTP_LENGTH }, (_, i) => (
                  <InputOTPSlot key={i} index={i} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>

          {state.error && <p className='text-sm text-red-600'>{state.error}</p>}

          <div className='flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between'>
            <button
              type='button'
              onClick={reset}
              className='cursor-pointer py-2 text-sm text-ink-soft hover:text-ink sm:py-0'
            >
              Cancel
            </button>
            <div className='flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-end sm:gap-4'>
              <button
                type='button'
                onClick={() => void resend()}
                disabled={resendCooldown > 0 || state.submitting}
                className='shrink-0 cursor-pointer text-sm font-medium text-accent transition-colors hover:text-[#15296b] disabled:cursor-not-allowed disabled:text-ink-muted dark:text-accent-mid'
              >
                {resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : 'Resend code'}
              </button>
              <button
                type='submit'
                disabled={
                  state.code.length !== EMAIL_CHANGE_OTP_LENGTH ||
                  state.submitting
                }
                className='flex-1 cursor-pointer rounded-xl bg-accent px-6 py-2.5 text-[14px] font-medium text-white transition-all hover:-translate-y-px hover:bg-[#15296b] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:bg-accent sm:flex-none'
              >
                {state.submitting
                  ? 'Checking...'
                  : state.step === 'current-code'
                    ? 'Continue'
                    : 'Change email'}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  )
}
